import { useState, useEffect, useMemo } from "react";
import { Select, Layout, Menu } from "antd";
import { DoubleLeftOutlined, DoubleRightOutlined } from "@ant-design/icons";
import { Link, Outlet, useNavigate, useLocation } from "react-router";

import { useUser } from "../../contexts/UserContext";
import { cleanTrialName } from "../../utils/stringUtils";
import { useScopeParams } from "../../hooks/useScopeParams";
import { getMenuItems } from "../../utils/menuItems";
import { getPortfolioMenuItems } from "../../utils/portfolioMenuItems";
import { useMenuFiltering } from "../../hooks/useMenuFiltering";
import { useRoleFilter } from "../../hooks/useRoleFilter";
import LogoIcon from "../../assets/icons/latest_vivo_logo.svg?react";
import VivoLogo from "../../assets/icons/vivo_logo.svg?react";
import ButtonMenu from "../../components/UI/ButtonMenu";
import HomeHeader from "../../components/UI/HomeHeader";
import UserMenu from "../../components/UI/UserMenu";
import Announcements from "../../components/ReleaseNotes/Announcements";
import { usePathNavigation } from "../../hooks/usePathNavigation";
import AskVivoDrawer from "../../components/Chat/AskVivoDrawer";
import useIsFeatureFlagEnabled from "../../hooks/useIsFeatureFlagEnabled";
import { HEADER_HEIGHT } from "../../utils/tableHelpers";
import { getLandingPageFromFeatureFlags } from "../../utils/landingPageUtils";
import { FEATURE_FLAGS } from "../../utils/feature-flag-constants";

import { useAdminFeatureAvailability } from "@/hooks/useAdminNavigation";

const { Header, Content, Sider } = Layout;

export default function TrialHome() {
  const navigate = useNavigate();
  const { scope, id, buildUrl } = useScopeParams();
  const { userInfo } = useUser();
  const { hasAdminAccess } = useAdminFeatureAvailability();
  const path = useLocation();

  return (
    <TrialHomeContent
      navigate={navigate}
      scope={scope}
      id={id}
      buildUrl={buildUrl}
      userInfo={userInfo}
      hasAdminAccess={hasAdminAccess}
      path={path}
    />
  );
}

function TrialHomeContent({
  navigate,
  scope,
  id,
  buildUrl,
  userInfo,
  hasAdminAccess,
  path,
}: any) {
  const isPortfolio = scope === "portfolio";
  const [collapsed, setCollapsed] = useState(false);
  const isFeatureFlagEnabled = useIsFeatureFlagEnabled();
  const visibleTrials = useMemo(
    () => userInfo?.trials ?? [],
    [userInfo?.trials],
  );
  const hasPortfolio = visibleTrials.length > 1;
  const trialSelectorOptions = useMemo(() => {
    const regularTrials = visibleTrials.map((trial: any) => ({
      label: `${trial.name ?? trial.id} | ${trial.id}`,
      value: trial.id,
    }));

    const portfolioOption = hasPortfolio
      ? [
          {
            label: "VIVO™ Clinical Portfolio",
            value: "PORTFOLIO",
          },
        ]
      : [];

    const adminOption = hasAdminAccess
      ? [
          {
            label: "VIVO™ Admin Settings",
            value: "admin-settings",
          },
        ]
      : [];

    return [...regularTrials, ...portfolioOption, ...adminOption];
  }, [hasAdminAccess, hasPortfolio, visibleTrials]);
  const selectedTrialValue = id;

  const { filterMainNavItems } = useRoleFilter();
  // Site-first tenants read the portfolio as a site-intelligence platform: the
  // nav leads with Sites, and Sites — not Trials — is where the portfolio opens.
  const siteFirstNav = isFeatureFlagEnabled(FEATURE_FLAGS.SITE_FIRST_NAV);
  const menuItems = isPortfolio
    ? getPortfolioMenuItems(siteFirstNav)
    : (getMenuItems as any)(collapsed);
  const featureFlagFiltered = useMenuFiltering(
    menuItems as any,
    isFeatureFlagEnabled,
  );
  const filteredMenuOptions = isPortfolio
    ? featureFlagFiltered
    : filterMainNavItems(featureFlagFiltered);
  const defaultLandingPage = getLandingPageFromFeatureFlags(
    isFeatureFlagEnabled,
    filteredMenuOptions,
  );
  const portfolioLandingPage = siteFirstNav ? "/site-profiles" : "/trials";
  const scopedDefaultLandingPage = isPortfolio
    ? portfolioLandingPage
    : defaultLandingPage;

  const { openMenu, newPathname } = usePathNavigation(
    path,
    scopedDefaultLandingPage,
  );
  const TRIAL_NAV_SUBMENU_KEYS = new Set([
    "/milestones",
    "/sites-metrics",
    "/enrollment",
    "/retention",
    "/safety",
    "/compliance",
  ]);
  const initialOpenKeys = TRIAL_NAV_SUBMENU_KEYS.has(openMenu)
    ? ["/trials"]
    : [openMenu];
  const [openKeys, setOpenKeys] = useState(initialOpenKeys);

  useEffect(() => {
    if (path.pathname === buildUrl()) {
      navigate(buildUrl(scopedDefaultLandingPage.replace(/^\//, "")), {
        replace: true,
      });
    }
  }, [navigate, path.pathname, buildUrl, scopedDefaultLandingPage]);

  const selectedTrial = userInfo?.trials?.find((trial: any) => trial.id === id);
  const selectedTrialLabel = cleanTrialName(
    selectedTrial?.name ?? selectedTrial?.id ?? id,
  );

  const portfolioHomeUrl = hasPortfolio
    ? `/portfolio${portfolioLandingPage}`
    : "/dashboard";

  const siderStyle = {
    height: "100vh",
    position: "fixed" as const,
  };

  const firstInitial = userInfo ? userInfo.first_name?.charAt(0) : "X";

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sider
        width={256}
        collapsible
        trigger={null}
        collapsed={collapsed}
        onCollapse={(value) => setCollapsed(value)}
        style={siderStyle}
        breakpoint="xl"
        collapsedWidth={80}
      >
        <div
          className={`flex flex-col h-full py-3 overflow-y-auto ${
            collapsed ? "px-5" : "px-6"
          } `}
        >
          <div>
            {collapsed ? (
              <div className="flex flex-col items-center mt-[24px] mb-3">
                <Link to={portfolioHomeUrl}>
                  <VivoLogo className="w-[32px] h-[18px]" />
                </Link>
              </div>
            ) : (
              <div className="flex flex-col mt-[24px] mb-3">
                <Link to={portfolioHomeUrl}>
                  <LogoIcon />
                </Link>
              </div>
            )}
          </div>

          {!isPortfolio && !collapsed && (
            <div className="mt-3">
              <Select
                data-testid="trial-navigation-select"
                placeholder="Select clinical trial"
                className="w-full"
                options={trialSelectorOptions}
                onSelect={(value) => {
                  if (String(value) === "admin-settings") {
                    navigate("/admin");
                    return;
                  }

                  if (String(value) === "PORTFOLIO") {
                    navigate(`/portfolio${portfolioLandingPage}`);
                    return;
                  }

                  navigate(`/trial/${value}`);
                }}
                value={selectedTrialValue}
              />
            </div>
          )}

          <div
            className={`flex-1 overflow-y-auto hide-scrollbar text-white ${
              collapsed ? "mt-2" : "mt-5"
            }`}
          >
            <Menu
              theme="dark"
              mode="inline"
              inlineIndent={3}
              defaultSelectedKeys={[""]}
              className="trial-main-nav"
              classNames={{
                popup: {
                  root: "trial-main-nav-popup",
                },
              }}
              tooltip={{
                classNames: {
                  root: "trial-main-nav-tooltip",
                },
              }}
              items={filteredMenuOptions as any}
              openKeys={openKeys}
              selectedKeys={[newPathname]}
              onOpenChange={(keys) => {
                setOpenKeys(keys);
              }}
            />
            <div className="flex flex-col">
              {!isPortfolio && !collapsed && (
                <Link
                  to={`/portfolio${portfolioLandingPage}`}
                  style={{ color: "white" }}
                >
                  <div className="flex text-sm px-1 mt-3">
                    <div className="flex gap-3 border-t border-[#556C75] p-2 w-full">
                      <p>All Trials</p>
                    </div>
                  </div>
                </Link>
              )}
            </div>
          </div>

          <div>
            {collapsed ? (
              <div className="flex flex-col items-center mb-4">
                <UserMenu mode="navigation">
                  <div className="bg-[#0E98D0] rounded-full h-7.5 w-7.5 flex justify-center items-center text-md border-none">
                    <span className="text-white">{firstInitial}</span>
                  </div>
                </UserMenu>
                <button
                  type="button"
                  onClick={() => setCollapsed(!collapsed)}
                  aria-label="Expand sidebar"
                  className="mt-5 h-10 w-10 shrink-0 flex items-center justify-center bg-[#ffffff1a] rounded-md text-white hover:bg-[#ffffff26] hover:text-white active:bg-[#ffffff33] active:scale-[0.98] transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                >
                  <DoubleRightOutlined className="text-inherit font-semibold" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 mt-5 mb-4">
                <div className="flex-1">
                  <UserMenu mode="navigation">
                    <ButtonMenu userInfo={userInfo} navbar={true} />
                  </UserMenu>
                </div>
                <button
                  type="button"
                  onClick={() => setCollapsed(!collapsed)}
                  aria-label="Collapse sidebar"
                  className="h-10 w-10 shrink-0 flex items-center justify-center bg-[#ffffff1a] rounded-[6px] text-white hover:bg-[#ffffff26] hover:text-white active:bg-[#ffffff33] active:scale-[0.98] transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                >
                  <DoubleLeftOutlined className="text-inherit font-semibold" />
                </button>
              </div>
            )}
          </div>
        </div>
      </Sider>
      <Layout
        className={`bg-[#EFF1F2] relative max-h-fit  ${
          collapsed ? "ml-[80px]" : "ml-[256px]"
        }`}
      >
        {!isPortfolio ? (
          <Header
            className="bg-white px-0 leading-none shadow-sm"
            style={{
              height: `${HEADER_HEIGHT}px`,
              position: "sticky",
              top: 0,
              right: 0,
              zIndex: 200,
              width: "100%",
            }}
          >
            <HomeHeader
              trialLabel={selectedTrialLabel}
              trialData={
                selectedTrial
                  ? selectedTrial.name?.toUpperCase().includes("ASCENT")
                    ? { ...selectedTrial, active: false }
                    : /cascade/i.test(selectedTrial.name ?? "")
                      ? { ...selectedTrial, active: true }
                      : selectedTrial
                  : {}
              }
            />
          </Header>
        ) : null}
        <Announcements />
        <Content
          id="page-content"
          style={{ overflow: "initial", background: "#EFF1F2" }}
        >
          <Outlet context={{ sidebarCollapsed: collapsed }} />
        </Content>
      </Layout>
      <AskVivoDrawer />
    </Layout>
  );
}
